import { C, BtnOrange, BtnNavy, metalGradient, scrollTo, useIsMobile } from '../styles';
import { PHOTOS, HERO_SLUG } from '../content/photos';

const specItems = [
  { k: 'Material', v: '304 Stainless Steel' },
  { k: 'Quantity', v: '25 units' },
  { k: 'Use case', v: 'Restaurant fixture' },
  { k: 'Timeline', v: '2–4 weeks' },
];

const trustItems = [
  { val: 'Under 72h', lbl: 'To your quote' },
  { val: '20+ shops', lbl: 'Ontario network' },
  { val: 'No CAD', lbl: 'A photo is enough' },
  { val: 'No fee', lbl: 'To get a quote' },
];

// The hero photograph is chosen by HERO_SLUG in content/photos.js — flipping that one
// value swaps this background and moves the losing candidate into the gallery.
// We take the largest hero crop at or below 1200px: the background is drawn under a
// heavy overlay, so a wider file buys nothing a desktop visitor can see.
const heroPhoto = PHOTOS[HERO_SLUG];
const heroWidth = Math.max(...heroPhoto.heroWidths.filter((w) => w <= 1200));
const heroUrl = `/images/${HERO_SLUG}-hero-${heroWidth}w.webp`;

export default function Hero() {
  const isMobile = useIsMobile();
  const headlineSize = isMobile ? 'clamp(40px, 13vw, 60px)' : 'clamp(48px, 7vw, 76px)';
  return (
    <section style={{
      padding: isMobile ? '28px 0 52px' : '80px 0 90px', position: 'relative',
      overflow: 'hidden', borderBottom: `1px solid ${C.bdr}`,
      // Desktop-only hero photo. On mobile the narrow, throttled connection
      // makes the background image add ~0.8s to LCP (measured), so we skip it
      // there and keep the fast text hero. Desktop keeps the photo.
      //
      // The overlay is a left-to-right ramp rather than the flat 72% the brief
      // suggests: the text all sits in the left column, so it gets 92-97% opacity
      // while the photograph stays legible on the right.
      //
      // THESE THREE STOPS ARE SET BY MEASUREMENT, NOT BY EYE. The previous ramp
      // (26,37,48 at .97/.88@42%/.60) put the headline at 2.87-2.96:1 against the
      // photo — below the 3:1 AA floor for large text — and the eyebrow and
      // standfirst below 4.5:1. This ramp is the LIGHTEST one that clears AA for
      // both hero candidates at 1440px: worst headline 4.02:1, everything else
      // above 4.5:1. Going lighter re-breaks it; going darker only buries more of
      // the photograph. Re-measure at 375px and 1440px before touching them.
      ...(isMobile ? {} : {
        backgroundImage: `linear-gradient(90deg, rgba(15,23,35,0.97) 0%, rgba(15,23,35,0.92) 55%, rgba(15,23,35,0.62) 100%), url('${heroUrl}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center right',
        backgroundRepeat: 'no-repeat',
      }),
    }}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', background: `radial-gradient(ellipse at top right, rgba(50,151,219,0.10) 0%, transparent 60%)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: '40%', height: '60%', background: `radial-gradient(ellipse at bottom left, rgba(232,76,61,0.06) 0%, transparent 65%)`, pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 36 : 52, alignItems: 'center' }}>

          {/* Left */}
          <div>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.navy, display: 'block', marginBottom: 12 }}>
              Ontario Custom Metal Fabrication
            </span>

            <div style={{ marginBottom: 20 }}>
              {['Send a photo.', 'Get a quote.'].map((line, i) => (
                <div key={i} style={{ lineHeight: 0.92, marginBottom: 6 }}>
                  <span style={{ ...metalGradient, fontSize: headlineSize }}>{line}</span>
                </div>
              ))}
              <div style={{ lineHeight: 0.92 }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: headlineSize, color: C.orange, letterSpacing: 3 }}>In 72 hours.</span>
              </div>
            </div>

            <p style={{ color: C.mut, fontSize: isMobile ? 15 : 16, lineHeight: 1.75, maxWidth: 430, marginBottom: 30 }}>
              Send a photo, sketch, or scan of the part you need. We confirm the specs with you, then turn it into a real, accurate quote — designed and built through our network of 20+ Ontario fabrication shops. From first prototype to full production. No CAD files. No fee to get a quote.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <BtnOrange onClick={() => scrollTo('submit')}>Get Quotes — Free</BtnOrange>
              <BtnNavy onClick={() => scrollTo('how')}>See How It Works</BtnNavy>
            </div>

            <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, gap: isMobile ? '18px 20px' : 28, marginTop: isMobile ? 32 : 40, paddingTop: 28, borderTop: `1px solid ${C.bdr}` }}>
              {trustItems.map(({ val, lbl }) => (
                <div key={lbl}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 20, fontWeight: 700, color: C.txt, textTransform: 'uppercase' }}>{val}</div>
                  {/* C.mut, not C.dim: C.dim gave these labels 2.45:1 on the flat
                      background and ~2.4:1 over the photo, both well under AA. */}
                  <div style={{ fontSize: 10, color: C.mut, letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 2 }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Brief card — an illustration of the deliverable, not a live project.
              The red status dot and the blue ACTIVE badge that used to sit here were
              removed deliberately: they simulated a running job on a page whose entire
              argument is now "nothing here is stock and nothing here is AI". Showing the
              *format* of what a customer receives is honest; dressing it as live traffic
              we do not have is the one kind of overstatement this audience punishes.
              Do not reintroduce ACTIVE, a status light, timestamps, or a job number. */}
          <div style={{ background: C.surf, border: `1px solid ${C.bdr2}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: C.surf2, borderBottom: `1px solid ${C.bdr}`, padding: '13px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, color: C.mut, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600 }}>Example Project Brief</span>
              <div style={{ marginLeft: 'auto', border: `1px solid ${C.bdr}`, borderRadius: 4, padding: '3px 10px', fontSize: 10, color: C.dim, fontWeight: 600, letterSpacing: '0.1em' }}>ILLUSTRATION</div>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, color: C.dim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>Project</div>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 21, fontWeight: 700, textTransform: 'uppercase', color: C.txt }}>Custom stainless support bracket</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 13 }}>
                {specItems.map(({ k, v }) => (
                  <div key={k} style={{ background: C.surf2, border: `1px solid ${C.bdr}`, borderRadius: 5, padding: '9px 12px' }}>
                    <div style={{ fontSize: 9, color: C.dim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: C.txt }}>{v}</div>
                  </div>
                ))}
              </div>
              {/* Describes what every customer gets back, rather than reporting the
                  state of a project that does not exist. */}
              <div style={{ background: C.navybg, border: `1px solid rgba(50,151,219,0.3)`, borderRadius: 6, padding: '10px 14px', marginBottom: 13 }}>
                <div style={{ fontSize: 9, color: C.navy, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>What you get back</div>
                <div style={{ fontSize: 12, color: C.mut }}>Confirmed specs and an accurate quote, in under 72 hours.</div>
              </div>
              <div style={{ border: `1.5px dashed ${C.bdr2}`, borderRadius: 6, padding: 18, textAlign: 'center' }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>✎</div>
                <div style={{ fontSize: 12, color: C.mut, fontWeight: 600 }}>Hand sketch attached</div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>Converted to structured brief + renderings</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
